import React from 'react';
import { BookOpenIcon } from '@heroicons/react/24/outline';

// Tutorial content for each game
const TUTORIALS = {
  ROT: {
    title: 'Cube Rotation (ROT)',
    introduction: `The Cube Rotation test measures your spatial orientation ability by mentally rotating a 3D cube and tracking which face ends up on top after hearing a sequence of directional commands.`,
    rules: [
      'Each round begins with an "Attention" prompt, followed by the announcement of an initial face (Top, Front, Bottom, Back, Left, Right).',
      'A sequence of 6–9 rotation commands (Left, Right, Front, Back) is then read aloud at the assigned speed.',
      'You must mentally follow each command, rotating an imaginary cube accordingly.',
      'After all commands are read, you select the face you believe is now on top from 6 options.',
      'In Level 1, there is a 5-second pause after the initial position is announced. Levels 2 and 3 have no pause.',
      'Level 2 provides feedback after each round. Level 3 (real test) provides no feedback.',
      'Using Visual Assist in Level 3 reduces your score multiplier from 1.0x to 0.8x.',
    ],
    testNotes: [],
    bestTechnique: ``,
  },

  RMS: {
    title: 'Running Memory Span (RMS)',
    introduction: `The Running Memory Span test evaluates your auditory short-term memory by recalling digits read aloud in reverse order.`,
    rules: [
      'Each round plays a sequence of digits (1–9) at the selected speed. No digit repeats consecutively.',
      'While the last two digits are being read, a numpad appears for input.',
      'You must enter the digits you remember in reverse order (last digit first).',
      'You may delete only the last 2 digits you entered — plan your input carefully.',
      'Scoring counts consecutive correct digits from the end of the reversed sequence until the first error.',
      'Your final score is the average across all 25 rounds.',
      'Level 1 plays at slow speed (1.2 digits/sec) with feedback. Level 2 is fast (1.6 digits/sec) with feedback. Level 3 is fast with no feedback.',
    ],
    testNotes: [],
    bestTechnique: ``,
  },

  OWT: {
    title: 'Perceptual Speed (OWT)',
    introduction: `The OWT Perceptual Speed test measures your ability to quickly identify and memorize pointer readings from instruments that match a critical rule (e.g., "Black instruments").`,
    rules: [
      'Before each round, an "Attention, Next item" screen is shown for 2.5 seconds.',
      'The critical parameter is then displayed for 2.5 seconds (e.g., "Black instruments").',
      'A 3×3 grid of 9 clock instruments is shown briefly (2.8–5 seconds depending on level).',
      'Exactly 4 of the 9 instruments match the critical rule. The other 5 do not.',
      'After the grid disappears, you must recall the pointer value (1–8) of each critical instrument in order (left-to-right, top-to-bottom).',
      'Each clock has 8 hour positions. Only 3 numbers are visible on each clock face.',
      'Level 1 is slow with feedback. Level 2 is medium with no feedback. Level 3 is fast with no feedback.',
    ],
    testNotes: [],
    bestTechnique: ``,
  },

  MEK: {
    title: 'Mechanical Comprehension (MEK)',
    introduction: `The MEK test assesses your understanding of basic mechanical and physical principles through multiple-choice questions.`,
    rules: [
      'Each round presents a mechanical reasoning question with an illustration.',
      'You select the correct answer from the provided options.',
      'Questions cover topics like gears, pulleys, levers, pressure, buoyancy, and basic physics.',
      'There is a time limit for each question.',
      'Your score is based on the number of correct answers.',
    ],
    testNotes: [],
    bestTechnique: ``,
  },

  MIC: {
    title: 'Memory & Concentration (MIC)',
    introduction: `The MIC test evaluates your ability to memorize and recall visual information under time pressure.`,
    rules: [
      'Each round presents visual information to memorize within a time limit.',
      'After the display phase, you must recall the information accurately.',
      'Questions test different types of visual memory and pattern recognition.',
      'Your score is based on accuracy of recall.',
    ],
    testNotes: [],
    bestTechnique: ``,
  },

  SKT: {
    title: 'Spatial Orientation (SKT)',
    introduction: `The SKT test measures your spatial reasoning ability by mentally manipulating and comparing 2D and 3D shapes.`,
    rules: [
      'Each round presents spatial reasoning questions involving shapes and patterns.',
      'You must identify relationships, rotations, or transformations between shapes.',
      'There is a time limit for each question.',
      'Your score is based on the number of correct answers within the time limit.',
    ],
    testNotes: [],
    bestTechnique: ``,
  },

  PPT: {
    title: 'Paper Folding Test (PPT)',
    introduction: `The Paper Folding Test assesses your spatial visualization abilities by matching 2D unfolded cube patterns to 3D cube representations.`,
    rules: [
      'Each task displays an illustration of a cube that has been cut open along its edges and opened up into a 2-dimensional object.',
      'Below the 2D pattern, 5 three-dimensional cubes are displayed.',
      'On every square of the cut-open cube, there is a certain dot pattern.',
      'The dot patterns differ from regular gambling dice - study the possible patterns carefully.',
      'Your task is to find which of the 5 displayed 3D cubes could be assembled from the given 2D object.',
      'There is only one correct solution to every item.',
      'You must compare the dot patterns and their orientation on the 3D cubes with the ones on the 2D object.',
      'The 2D object is not transparent - you are looking at its surface. To fold a cube out of it, the square areas must be folded backwards.',
      'Level 1 has no time limit. Level 2 has a 20-minute time limit for all 20 tasks.',
      'Each run consists of 20 tasks.',
    ],
    testNotes: [
      'Pay careful attention to the orientation of dot patterns, not just the number of dots.',
      'Remember that when folding the 2D pattern, some faces will be rotated relative to each other.',
      'Work systematically through each visible face of the 3D cubes.',
      'Eliminate obviously incorrect answers first to narrow down your choices.',
    ],
    bestTechnique: `Start by identifying unique or distinctive dot patterns on the 2D layout. Then check each 3D cube option systematically, comparing the visible faces (top, front, right side) with the corresponding areas on the unfolded pattern. Pay special attention to how the dots are oriented - a pattern with 3 dots in a diagonal line can be rotated, so orientation matters. Eliminate cubes that have incorrect patterns or orientations on any visible face.`,
  },
};

const GameTutorial = ({ gameCode }) => {
  const tutorial = TUTORIALS[gameCode];

  if (!tutorial) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500">
        <BookOpenIcon className="h-10 w-10 mb-3 opacity-40" />
        <p className="text-sm font-medium">Tutorial not available</p>
        <p className="text-xs mt-1">Tutorial content for this game is coming soon.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto pr-1 space-y-5">
      {/* Introduction */}
      <section>
        <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-2">Introduction</h4>
        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{tutorial.introduction}</p>
      </section>

      {/* Rules */}
      <section>
        <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-2">Rules</h4>
        <ul className="space-y-2">
          {tutorial.rules.map((rule, i) => (
            <li key={i} className="flex items-start text-sm text-gray-600 dark:text-gray-300">
              <span className="text-primary-500 mr-2 mt-0.5 flex-shrink-0">•</span>
              <span className="leading-relaxed">{rule}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Real Life Test Notes */}
      <section>
        <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-2">Real Life Test Notes</h4>
        {tutorial.testNotes.length > 0 ? (
          <ul className="space-y-2">
            {tutorial.testNotes.map((note, i) => (
              <li key={i} className="flex items-start text-sm text-gray-600 dark:text-gray-300">
                <span className="text-amber-500 mr-2 mt-0.5 flex-shrink-0">•</span>
                <span className="leading-relaxed">{note}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500 italic">Coming soon...</p>
        )}
      </section>

      {/* Best Technique */}
      <section>
        <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-2">Best Technique</h4>
        {tutorial.bestTechnique ? (
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{tutorial.bestTechnique}</p>
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500 italic">Coming soon...</p>
        )}
      </section>
    </div>
  );
};

export { TUTORIALS };
export default GameTutorial;
